import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Layout } from "react-grid-layout";
import _ from "lodash";

import useShallowSelector from "./useShalowSelector";
import { setHeight as setFieldHeight, updateLayoutRequest } from "./../store/layout/actionCreators";
import { selectLayout } from "./../store/layout/selectors";
import { ILayoutList, IUpdateLayoutWithResponsiveData } from "./../store/layout/types";
import { getScreenType } from "./../helpers/common";
import { selectFields } from "./../store/fields/selectors";
import { IField } from "../store/fields/types";
import { getLayoutWithUpdatedWidths } from "../helpers/layoutActions";

export interface IOnChnage {
    (layout: Layout[]): void;
}

export interface ISetHeight {
    (id: IField["_id"], height: number): void;
}

export type IOnUpdateLayoutWithResponsive = (
    data?: Omit<IUpdateLayoutWithResponsiveData, "screenByType" & "fields">,
) => void;

type UseLayout = () => {
    layout: ILayoutList;
    edited: number;
    onChange: IOnChnage;
    setHeight: ISetHeight;
    updateLayoutWidths: IOnUpdateLayoutWithResponsive;
};

const useLayout: UseLayout = () => {
    const dispatch = useDispatch();
    const { list, edited } = useShallowSelector(selectLayout);
    const fields = useShallowSelector(selectFields);
    const referenceLayoutData = useRef(list);

    useEffect(() => {
        if (Object.keys(referenceLayoutData.current).length === 0 && Object.keys(list).length > 0)
            referenceLayoutData.current = list;
    }, [referenceLayoutData, list]);

    const onChange: IOnChnage = layout => {
        const dimensions: ILayoutList = {};
        const filteredReferenceLayoutData: ILayoutList = {};

        layout.forEach(item => {
            const field_id = item.i.split(".")[0] as IField["_id"];
            filteredReferenceLayoutData[field_id] = referenceLayoutData.current[field_id];
            dimensions[field_id] = {
                h: item.h,
                w: item.w,
                x: item.x,
                y: item.y,
                minH: item.minH,
                maxH: item.maxH,
                minW: item.minW,
                maxW: item.maxW,
            };
        });

        if (Object.keys(dimensions).length > 0 && !_.isEqual(filteredReferenceLayoutData, dimensions)) {
            dispatch(updateLayoutRequest({ layout: dimensions }));
            referenceLayoutData.current = { ...referenceLayoutData.current, ...dimensions };
        }
    };

    const setHeight: ISetHeight = (id, height) => {
        if (list[id] && list[id].h !== height && list[id].maxH !== height && list[id].minH !== height) {
            dispatch(setFieldHeight({ id, height }));
            referenceLayoutData.current[id] = {
                ...referenceLayoutData.current[id],
                h: height,
                maxH: height,
                minH: height,
            };
        }
    };

    const updateLayoutWidths: IOnUpdateLayoutWithResponsive = data => {
        if (data === undefined) return;

        let newLayout = null;
        if (data && data.id) {
            const { id, ...changedValue } = data;
            newLayout = getLayoutWithUpdatedWidths(list, fields, getScreenType(), id, changedValue);
        } else newLayout = getLayoutWithUpdatedWidths(list, fields, getScreenType());

        if (!_.isEqual(referenceLayoutData.current, newLayout)) {
            dispatch(updateLayoutRequest({ layout: newLayout }));
            referenceLayoutData.current = { ...referenceLayoutData.current, ...newLayout };
        }
    };

    return {
        layout: list,
        edited,
        onChange,
        setHeight,
        updateLayoutWidths,
    };
};

export default useLayout;
